import {
  DEMOTE_SCORE,
  MASTERY_CONFIG,
  PROMOTE_SCORE,
  REVIEW_INTERVAL_DAYS,
} from '@/config/learning';
import type { LearningObjective } from '@/content/schema';
import type { MasteryStatus, MistakeType } from '@/domain/ids';
import type { ExerciseAttempt, MasteryRecord } from '@/domain/records';
import { DAY_MS, localDayKey, toIso } from '@/lib/time';

export type MasteryEstimate = Omit<MasteryRecord, 'learnerId' | 'objectiveId' | 'updatedAt'>;

const byTime = (a: ExerciseAttempt, b: ExerciseAttempt) => a.answeredAt.localeCompare(b.answeredAt);

function credit(attempt: ExerciseAttempt, hintCredit: number): number {
  if (!attempt.correct) return 0;
  return attempt.hintUsed ? hintCredit : 1;
}

function weightedScore(attempts: ExerciseAttempt[], hintCredit: number): number {
  const weight = attempts.reduce((sum, a) => sum + a.evidenceWeight, 0);
  if (weight === 0) return 0;
  return attempts.reduce((sum, a) => sum + credit(a, hintCredit) * a.evidenceWeight, 0) / weight;
}

function mostFrequent(values: MistakeType[]): MistakeType | undefined {
  const counts = new Map<MistakeType, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  const known = [...counts.entries()].filter(([type]) => type !== 'unknown');
  const pool = known.length > 0 ? known : [...counts.entries()];
  return pool.sort((a, b) => b[1] - a[1])[0]?.[0];
}

/**
 * Replays practice sessions in order through Leitner boxes. Promotion happens
 * at most once per day, so cramming does not jump straight to long intervals.
 */
function spacedRepetition(firstTries: ExerciseAttempt[], hintCredit: number) {
  const sessions = new Map<string, ExerciseAttempt[]>();
  for (const attempt of firstTries) {
    const list = sessions.get(attempt.sessionId) ?? [];
    list.push(attempt);
    sessions.set(attempt.sessionId, list);
  }
  let box = 0;
  let lastPromotionDay: string | undefined;
  const ordered = [...sessions.values()].sort((a, b) => byTime(a[0]!, b[0]!));
  for (const session of ordered) {
    const score = weightedScore(session, hintCredit);
    const day = localDayKey(session[0]!.answeredAt);
    if (score >= PROMOTE_SCORE) {
      if (lastPromotionDay !== day) {
        box = Math.min(box + 1, REVIEW_INTERVAL_DAYS.length - 1);
        lastPromotionDay = day;
      } else {
        box = Math.max(box, 1);
      }
    } else if (score < DEMOTE_SCORE) {
      box = 1;
    } else {
      box = Math.max(box, 1);
    }
  }
  return box;
}

/**
 * Explainable mastery estimate for one objective, computed only from stored
 * attempts. It is an estimate of observed practice, never a judgement of the child.
 */
export function computeMastery(
  attempts: ExerciseAttempt[],
  objective?: Pick<LearningObjective, 'mastery'>,
  config = MASTERY_CONFIG,
): MasteryEstimate {
  const masteryScore = objective?.mastery?.accuracy ?? config.masteryScore;
  const masteryMinEvidence = objective?.mastery?.minAttempts ?? config.masteryMinEvidence;
  const sorted = [...attempts].filter((a) => !a.timedOut).sort(byTime);
  const firstTries = sorted.filter((a) => a.attemptNumber === 1);
  const recent = firstTries.slice(-config.recentWindow);
  const evidence = firstTries.reduce((sum, a) => sum + a.evidenceWeight, 0);
  const recentScore = weightedScore(recent, config.hintCredit);
  const distinctDays = new Set(firstTries.map((a) => localDayKey(a.answeredAt))).size;
  const sessions = new Set(firstTries.map((a) => a.sessionId)).size;
  const recentMistakes = sorted
    .slice(-config.recentWindow * 2)
    .filter((a) => !a.correct && a.mistakeType)
    .map((a) => a.mistakeType as MistakeType);
  const topMistake = mostFrequent(recentMistakes);

  let trend: MasteryEstimate['trend'] = 'unknown';
  if (recent.length >= 6) {
    const half = Math.floor(recent.length / 2);
    const delta =
      weightedScore(recent.slice(half), config.hintCredit) -
      weightedScore(recent.slice(0, half), config.hintCredit);
    trend =
      delta > config.trendDelta ? 'improving' : delta < -config.trendDelta ? 'declining' : 'stable';
  }

  const percent = Math.round(recentScore * 100);
  let status: MasteryStatus;
  let reason: MasteryEstimate['reason'];
  if (firstTries.length === 0) {
    status = 'notStarted';
    reason = { code: 'notStarted', params: {} };
  } else if (evidence < config.minEvidence) {
    status = 'learning';
    reason = { code: 'gatheringEvidence', params: { answered: firstTries.length } };
  } else if (
    recentScore >= masteryScore &&
    evidence >= masteryMinEvidence &&
    distinctDays >= config.masteryMinDays
  ) {
    status = 'provisionalMastery';
    reason = { code: 'consistentAcrossDays', params: { percent, days: distinctDays } };
  } else if (recentScore >= masteryScore) {
    status = 'progressing';
    reason = { code: 'needsConfirmation', params: { percent } };
  } else if (recentScore >= config.progressingScore) {
    status = 'progressing';
    reason = { code: 'mostlyCorrect', params: { percent } };
  } else if (recentScore < config.needsPracticeScore) {
    status = 'needsPractice';
    reason = {
      code: 'frequentMistakes',
      params: { percent, ...(topMistake ? { mistake: topMistake } : {}) },
    };
  } else {
    status = 'learning';
    reason = { code: 'mixedResults', params: { percent } };
  }

  const box = firstTries.length > 0 ? spacedRepetition(firstTries, config.hintCredit) : 0;
  const lastPracticedAt = sorted.at(-1)?.answeredAt;
  const nextReviewAt =
    lastPracticedAt && box > 0
      ? toIso(new Date(lastPracticedAt).getTime() + (REVIEW_INTERVAL_DAYS[box] ?? 1) * DAY_MS)
      : undefined;

  return {
    status,
    recentScore,
    attempts: firstTries.length,
    firstTryCorrect: firstTries.filter((a) => a.correct).length,
    incorrect: sorted.filter((a) => !a.correct).length,
    hintsUsed: firstTries.filter((a) => a.hintUsed).length,
    retriesCorrect: sorted.filter((a) => a.attemptNumber > 1 && a.correct).length,
    sessions,
    distinctDays,
    trend,
    ...(topMistake ? { topMistake } : {}),
    reason,
    box,
    ...(lastPracticedAt ? { lastPracticedAt } : {}),
    ...(nextReviewAt ? { nextReviewAt } : {}),
  };
}

export const STATUS_RANK: Record<MasteryStatus, number> = {
  notStarted: 0,
  needsPractice: 1,
  learning: 2,
  progressing: 3,
  provisionalMastery: 4,
};

export const isSecure = (status: MasteryStatus | undefined) =>
  status === 'progressing' || status === 'provisionalMastery';

export function isDue(record: Pick<MasteryRecord, 'nextReviewAt' | 'status'>, now: Date): boolean {
  return isSecure(record.status) && !!record.nextReviewAt && new Date(record.nextReviewAt) <= now;
}
